import React, { useState, useEffect, useRef, useCallback, useMemo, useContext } from 'react';
import { LangContext, useLang } from '../../contexts/LangContext.js';
import {
  LineChart, BarChart, AreaChart, RadarChart, ScatterChart,
  Line, Bar, Area, Radar, Scatter,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine,
  ResponsiveContainer, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  Cell, ComposedChart,
} from 'recharts';
import { COLORS_DARK, COLORS_LIGHT } from '../shared/theme.js';
import { useTheme } from '../../contexts/ThemeContext.js';

// COLORS: LangContext/ThemeContext 없이도 동작하는 런타임 참조
// V49 메인의 isDark 상태에 따라 동적으로 결정되나,
// 탭 파일은 일단 COLORS_LIGHT를 기본값으로 사용
// (isDark props가 필요하면 추후 ThemeContext로 교체)

// LangContext — V49 메인의 Provider 안에서 렌더되므로 동작함

// ============================================================
// CSVModal.js
// EV Powertrain Simulator — Phase 4 컴포넌트 분리
// CSV 내보내기 모달
// ============================================================

// ── CSVModal ──
function CSVModal({ show, onClose, compResults, paramsA, paramsB, reaccelV0 = 60, reaccelV1 = 100 }) {
  const COLORS = useTheme();
  const lang = useLang();
  const e = (ko, en) => lang === "en" ? en : ko;
  if (!show || !compResults) return null;
  const c = compResults;

  const datasets = [
    { id: "launch300_A", icon: "🚀", label: `${e("발진 0→300","Launch 0→300")} (${paramsA.shortName})`, labelEn: "Launch 0→300",
      data: c.a?.launch300?.data, color: COLORS.vehA },
    { id: "launch300_B", icon: "🚀", label: `${e("발진 0→300","Launch 0→300")} (${paramsB.shortName})`, labelEn: "Launch 0→300",
      data: c.b?.launch300?.data, color: COLORS.vehB },
    { id: "launch150_A", icon: "🚀", label: `${e("발진 50%","Launch 50%")} (${paramsA.shortName})`, labelEn: "Launch 50%",
      data: c.a?.launch150?.data, color: COLORS.vehA },
    { id: "reaccel100_A", icon: "⏩", label: `${e("재가속","Re-Accel")} ${reaccelV0}→${reaccelV1} (${paramsA.shortName})`, labelEn: "Re-Accel 100%",
      data: c.a?.reaccel100?.data, color: COLORS.vehA },
    { id: "reaccel100_B", icon: "⏩", label: `${e("재가속","Re-Accel")} ${reaccelV0}→${reaccelV1} (${paramsB.shortName})`, labelEn: "Re-Accel 100%",
      data: c.b?.reaccel100?.data, color: COLORS.vehB },
    { id: "coast_A", icon: "📉", label: `${e("코스트 100→0","Coast-Down 100→0")} (${paramsA.shortName})`, labelEn: "Coast-Down",
      data: c.a?.coast?.data, color: COLORS.vehA },
    { id: "coast_B", icon: "📉", label: `${e("코스트 100→0","Coast-Down 100→0")} (${paramsB.shortName})`, labelEn: "Coast-Down",
      data: c.b?.coast?.data, color: COLORS.vehB },
    { id: "brake100_A", icon: "🔄", label: `${e("회생제동 100","Regen Brake 100")} (${paramsA.shortName})`, labelEn: "Regen Brake",
      data: c.a?.brake100?.data, color: COLORS.vehA },
    { id: "brake100_B", icon: "🔄", label: `${e("회생제동 100","Regen Brake 100")} (${paramsB.shortName})`, labelEn: "Regen Brake",
      data: c.b?.brake100?.data, color: COLORS.vehB },
    { id: "roadload_A", icon: "⚡", label: `${e("정속 주행","Road Load")} (${paramsA.shortName})`, labelEn: "Road Load",
      data: c.a?.roadload, color: COLORS.vehA },
    { id: "roadload_B", icon: "⚡", label: `${e("정속 주행","Road Load")} (${paramsB.shortName})`, labelEn: "Road Load",
      data: c.b?.roadload, color: COLORS.vehB },
    { id: "effmap_A", icon: "🔋", label: `${e("효율 vs 속도","Efficiency vs Speed")} (${paramsA.shortName})`, labelEn: "Efficiency Map",
      data: c.a?.steadyEff, color: COLORS.vehA },
    { id: "effmap_B", icon: "🔋", label: `${e("효율 vs 속도","Efficiency vs Speed")} (${paramsB.shortName})`, labelEn: "Efficiency Map",
      data: c.b?.steadyEff, color: COLORS.vehB },
    { id: "energy_A", icon: "⛽", label: `${e("전비/항속","Energy/Range")} (${paramsA.shortName})`, labelEn: "Energy Consumption",
      data: c.a?.energyCons, color: COLORS.vehA },
    { id: "energy_B", icon: "⛽", label: `${e("전비/항속","Energy/Range")} (${paramsB.shortName})`, labelEn: "Energy Consumption",
      data: c.b?.energyCons, color: COLORS.vehB },
    { id: "thermal_A", icon: "🌡️", label: `${e("열 스트레스","Thermal Stress")} (${paramsA.shortName})`, labelEn: "Thermal Stress",
      data: c.a?.thermalStress, color: COLORS.vehA },
    { id: "thermal_B", icon: "🌡️", label: `${e("열 스트레스","Thermal Stress")} (${paramsB.shortName})`, labelEn: "Thermal Stress",
      data: c.b?.thermalStress, color: COLORS.vehB },
    { id: "sensitivity_A", icon: "📊", label: `${e("민감도 분석","Sensitivity Analysis")} (${paramsA.shortName})`, labelEn: "Sensitivity",
      data: c.a?.sens?.centralDiff, color: COLORS.vehA },
    { id: "compare_summary", icon: "🏁", label: e("비교 요약","Comparison Summary"), labelEn: "Comparison Summary",
      data: c.summary?.map(r => ({ metric: r.metric, metricEn: r.metricEn, [paramsA.shortName]: r.a, [paramsB.shortName]: r.b, unit: r.unitLabel })),
      color: COLORS.accent },
    { id: "wltp_A", icon: "🛣️", label: `${paramsA.cycle_type||'WLTP'} ${e("사이클","Cycle")} (${paramsA.shortName})`, labelEn: paramsA.cycle_type||"WLTP",
      data: c.a?.wltp?.data, color: COLORS.vehA },
    { id: "wltp_B", icon: "🛣️", label: `${paramsB.cycle_type||'WLTP'} ${e("사이클","Cycle")} (${paramsB.shortName})`, labelEn: paramsB.cycle_type||"WLTP",
      data: c.b?.wltp?.data, color: COLORS.vehB },
    { id: "wltp_tempsens_A", icon: "🌡️", label: `${e("온도 민감도","Temp Sensitivity")} (${paramsA.shortName})`, labelEn: "TempSens",
      data: c.a?.wltpTempSens, color: COLORS.vehA },
    { id: "wltp_tempsens_B", icon: "🌡️", label: `${e("온도 민감도","Temp Sensitivity")} (${paramsB.shortName})`, labelEn: "TempSens",
      data: c.b?.wltpTempSens, color: COLORS.vehB },
    // [v8-fix5] CSV Full-Spec 사이클 데이터
    { id: "csv_cycle_A", icon: "📋", label: `${e("CSV 사이클","CSV Full-Spec Cycle")} (${paramsA.shortName})`, labelEn: "CSV Full-Spec Cycle",
      data: c.a?.csvCycleResult?.data, color: COLORS.vehA },
    { id: "csv_cycle_B", icon: "📋", label: `${e("CSV 사이클","CSV Full-Spec Cycle")} (${paramsB.shortName})`, labelEn: "CSV Full-Spec Cycle",
      data: c.b?.csvCycleResult?.data, color: COLORS.vehB },
  ];

  const handleDownload = (ds) => {
    if (!ds.data || ds.data.length === 0) return;
    const csv = generateCSV(ds.data, undefined, lang);
    downloadCSV(`ev_sim_${ds.id}_${new Date().toISOString().slice(0,10)}.csv`, csv);
  };

  const handleDownloadAll = () => {
    datasets.forEach(ds => {
      if (ds.data && ds.data.length > 0) handleDownload(ds);
    });
  };

  return (
    <div style={modalOverlay} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ ...modalBox, background: COLORS.panel, border: `1px solid ${COLORS.border}`, color: COLORS.text }} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={{ padding: "14px 20px", borderBottom: `1px solid ${COLORS.border}`,
          display: "flex", justifyContent: "space-between", alignItems: "center",
          background: `linear-gradient(135deg, ${COLORS.panel}, ${COLORS.bg})`, borderRadius: "12px 12px 0 0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 22 }}>📥</span>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.text }}>{e("시뮬레이션 데이터 다운로드","Simulation Data Download")}</div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: COLORS.textDim, fontSize: 22, cursor: "pointer", lineHeight: 1 }}>✕</button>
        </div>

        {/* Download all button */}
        <div style={{ padding: "12px 20px", borderBottom: `1px solid ${COLORS.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 11, color: COLORS.textMuted, fontFamily: mono }}>
            {datasets.filter(d => d.data && d.data.length > 0).length} {e("개 데이터셋 사용 가능","datasets available")}
          </div>
          <button onClick={handleDownloadAll}
            style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 20px", borderRadius: 6, cursor: "pointer",
              background: `linear-gradient(135deg, ${COLORS.accent3}, #059669)`,
              border: "none", color: "#000", fontSize: 12, fontWeight: 700 }}>
            {e("📦 전체 다운로드", "📦 Download All")}
          </button>
        </div>

        {/* Dataset list */}
        <div style={{ padding: "12px 20px", overflowY: "auto", overflowX: "hidden", flex: 1, maxHeight: "55vh" }}>
          <div style={{ display: "grid", minWidth: 0, gridTemplateColumns: "repeat(auto-fit, minmax(min(200px, 45vw), 1fr))", gap: 8 }}>
            {datasets.map((ds, i) => {
              const hasData = ds.data && ds.data.length > 0;
              const rowCount = hasData ? ds.data.length : 0;
              const colCount = hasData ? Object.keys(ds.data[0]).length : 0;
              return (
                <button key={i}
                  onClick={() => hasData && handleDownload(ds)}
                  disabled={!hasData}
                  style={{
                    display: "flex", alignItems: "center", gap: 10, padding: "10px 12px",
                    borderRadius: 8, cursor: hasData ? "pointer" : "not-allowed",
                    background: hasData ? `${ds.color}08` : `${COLORS.bg}`,
                    border: `1px solid ${hasData ? ds.color + "40" : COLORS.border}`,
                    opacity: hasData ? 1 : 0.4, transition: "all 0.15s", textAlign: "left",
                  }}
                  onMouseOver={(e) => { if (hasData) e.currentTarget.style.borderColor = ds.color; }}
                  onMouseOut={(e) => { if (hasData) e.currentTarget.style.borderColor = ds.color + "40"; }}>
                  <span style={{ fontSize: 20, flexShrink: 0 }}>{ds.icon}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: ds.color, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{ds.label}</div>
                    <div style={{ fontSize: 9, color: COLORS.textMuted, fontFamily: mono }}>{ds.labelEn}</div>
                    {hasData && (
                      <div style={{ fontSize: 8, color: COLORS.textDim, fontFamily: mono, marginTop: 2 }}>
                        {rowCount} rows × {colCount} cols
                      </div>
                    )}
                  </div>
                  {hasData && <span style={{ fontSize: 14, color: ds.color, flexShrink: 0 }}>⬇</span>}
                </button>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: "10px 20px", borderTop: `1px solid ${COLORS.border}`, fontSize: 10, color: COLORS.textMuted, fontFamily: mono }}>
          {e("💡 CSV 파일은 UTF-8 (BOM) 인코딩으로 저장됩니다. Excel에서 바로 열 수 있습니다.","💡 Files are saved in UTF-8 (BOM) encoding. Compatible with Excel.")}
        </div>
      </div>
    </div>
  );
}

export {
  CSVModal,
};